/**
 * useGlobalSearch — top-bar cross-entity search (Prompt 11F.2).
 *
 * Debounces the input, then queries GET /v1/search. Respects the owner
 * tenant switcher's "All" mode (D9) the same way EntityTable list pages do
 * (see useTenantAwareEntityList.ts): one request per tenant with an
 * explicit `X-Tenant-Id` header override, merged client-side. There is no
 * server-side "all tenants" concept — every request still targets exactly
 * one tenant, validated independently by the backend.
 */

import { useEffect, useRef, useState } from 'react';
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiCall } from '@core/api/client';
import { useTenant } from '@core/tenant/TenantContext';

export type SearchEntityType = 'students' | 'staff' | 'invoices' | 'packages' | 'subjects';

export interface SearchResultItem {
  id: string;
  title: string;
  subtitle?: string | null;
  tenant_id: string;
  path: string;
}

export interface SearchGroup {
  type: SearchEntityType;
  label: string;
  results: SearchResultItem[];
}

interface SearchResponse {
  query: string;
  tenant_id: string;
  groups: SearchGroup[];
}

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const PER_TYPE_LIMIT = 5;

function fetchSearch(q: string, tenantId?: string): Promise<SearchResponse> {
  return apiCall<SearchResponse>('GET', '/v1/search', undefined, {
    query: { q, limit: PER_TYPE_LIMIT },
    headers: tenantId ? { 'X-Tenant-Id': tenantId } : undefined,
  });
}

function mergeGroups(responses: SearchResponse[]): SearchGroup[] {
  const byType = new Map<SearchEntityType, SearchGroup>();
  for (const res of responses) {
    for (const group of res.groups) {
      const existing = byType.get(group.type);
      if (existing) {
        existing.results.push(...group.results);
      } else {
        byType.set(group.type, { ...group, results: [...group.results] });
      }
    }
  }
  return [...byType.values()];
}

export interface UseGlobalSearchResult {
  inputValue: string;
  setInputValue: (value: string) => void;
  /** True once the debounced query meets the minimum length to search. */
  hasQuery: boolean;
  isLoading: boolean;
  groups: SearchGroup[];
}

export function useGlobalSearch(): UseGlobalSearchResult {
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedQuery(inputValue.trim()), DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [inputValue]);

  const { isAllMode, availableTenantIds, selectedTenantId } = useTenant();
  const hasQuery = debouncedQuery.length >= MIN_QUERY_LENGTH;

  const singleQuery = useQuery({
    queryKey: ['global-search', 'single', debouncedQuery, selectedTenantId],
    queryFn: () => fetchSearch(debouncedQuery),
    enabled: hasQuery && !isAllMode,
    placeholderData: (prev) => prev,
  });

  const fanOutQueries = useQueries({
    queries:
      hasQuery && isAllMode
        ? availableTenantIds.map((tenantId) => ({
            queryKey: ['global-search', 'tenant', debouncedQuery, tenantId],
            queryFn: () => fetchSearch(debouncedQuery, tenantId),
          }))
        : [],
  }) as UseQueryResult<SearchResponse, Error>[];

  const isLoading = isAllMode
    ? fanOutQueries.some((q) => q.isLoading)
    : singleQuery.isLoading;

  const groups: SearchGroup[] = !hasQuery
    ? []
    : isAllMode
      ? mergeGroups(fanOutQueries.map((q) => q.data).filter((d): d is SearchResponse => !!d))
      : (singleQuery.data?.groups ?? []);

  return { inputValue, setInputValue, hasQuery, isLoading, groups };
}
